import './ChangePassword.scss';

import React, { useState, SyntheticEvent } from 'react';
import { Form, Button } from 'react-bootstrap';
import validate from 'validate.js';

import { UseTranslate } from '../../hooks/Translate';
import { useMergedState } from '../../hooks/useMergedState';

import { IBaseForm } from '../../types/types';
import { changePasswordFormConstraints } from '../../constraints/ChangePasswordForm';

import Alert, { AlertType, IAlertProps } from '../components/Alert';
import RequestHelper from '../../apis/RequestHelper';

interface IRegisterFormState extends IBaseForm {
  focus: Record<string, boolean>;
}

export default function ChangePassword() {
  const [translate] = UseTranslate();

  const constraintsMap = changePasswordFormConstraints(
    translate('cas.change-password.pswdRequired'),
    translate('cas.change-password.pswdMin6Char'),
    translate('cas.change-password.pswdRequired'),
    translate('cas.change-password.pswdDontMatch')
  );

  const [state, setState] = useMergedState<IRegisterFormState>({
    focus: {},
    errors: {},
    validated: false,
  });

  const [alertProps, setAlertProps] = useState<IAlertProps>({
    type: AlertType.NONE,
  });

  const onChangePassword = (key = 'newPassword') => {
    const newState = Object.assign({}, state);
    newState.focus[key] = true;

    const { result, elements } = validatePassword(newState);

    if (result == null) {
      newState.errors = {};
    } else if (result[key]) {
      Object.assign(newState.errors, result);
    } else {
      delete newState.errors[key];
    }

    setState(newState);

    for (const key in elements) {
      if (result && typeof result[key] === 'object') {
        elements[key].classList.add('is-invalid');
      } else {
        elements[key].classList.remove('is-invalid');
      }
    }
  };

  const validatePassword = ({ focus }: IRegisterFormState) => {
    const elements: { [key: string]: HTMLInputElement } = {};
    const attributes: { [key: string]: string } = {};
    const constraints: { [key: string]: string } = {};

    for (const key of ['currentPassword', 'newPassword', 'repeatPassword']) {
      const selector = '[name="' + key + '"]';
      const element = document.querySelector(selector) as HTMLInputElement;
      if (focus[key]) {
        elements[key] = element;
        attributes[key] = element.value;
        constraints[key] = constraintsMap[key];
      }
    }

    const result = validate(attributes, constraints, { fullMessages: false });

    return {
      elements,
      attributes,
      result,
    };
  };

  const togglePassword = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.currentTarget.classList.toggle('toggle-visible-password--visible');
    const previousSibling = e.currentTarget.previousSibling as HTMLInputElement;
    const type = previousSibling.getAttribute('type');
    switch (type) {
      case 'text':
        previousSibling.setAttribute('type', 'password');
        break;
      case 'password':
        previousSibling.setAttribute('type', 'text');
        break;
    }
  };

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();

    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());

    const result = validate(
      values,
      changePasswordFormConstraints(
        translate('cas.change-password.pswdRequired'),
        translate('cas.change-password.pswdMin6Char'),
        translate('cas.change-password.pswdRequired'),
        translate('cas.change-password.pswdDontMatch')
      ),
      { fullMessages: false }
    );

    if (result) {
      setState({ errors: result });
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.change-password.submitError'),
        message: translate(
          'cas.change-password.changePasswordCheckErrorFields'
        ),
      });
    } else {
      setState({ errors: {} });
      changePassword(values);
    }

    setState({ validated: true });
  };

  async function changePassword(data: any) {
    try {
      const res = await RequestHelper.cas.put('/users/password/change', data);
      if (res.data.status === 'SUCCESS') {
        setAlertProps({
          type: AlertType.SUCCESS,
          title: translate('cas.change-password.success'),
          message: res.data.data.message,
        });
      }
    } catch (e) {
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.change-password.submitError'),
        message: String(e)
      });
    }
  }

  return (
    <>
      <div className="cas-content cas-change-password">
        <h5>პარამეტრები</h5>
        <Alert
          type={alertProps!.type}
          title={alertProps!.title}
          message={alertProps!.message}
        />
        <Form validated={state.validated} onSubmit={onFormSubmit} noValidate>
          <Form.Group className='mb-3 position-relative' controlId="currentPassword">
            <Form.Label className="small password-icon">
              {translate('cas.change-password.curPswd')}
            </Form.Label>
            <Form.Control
              type="password"
              name="currentPassword"
              placeholder={translate('cas.change-password.inputCurPswd')}
              autoComplete="current-password"
              isInvalid={!!state.errors.currentPassword}
              onChange={() => onChangePassword('currentPassword')}
              required
            />
            <span
              className="toggle-visible-password"
              onClick={togglePassword}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors.currentPassword && state.errors.currentPassword[0]}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className='mb-3 position-relative' controlId="newPassword">
            <Form.Label className="small password-icon">
              {translate('cas.change-password.newPswd')}
            </Form.Label>
            <Form.Control
              type="password"
              name="newPassword"
              placeholder={translate('cas.change-password.inputPswd')}
              autoComplete="new-password"
              isInvalid={!!state.errors.newPassword}
              onChange={() => onChangePassword()}
              required
            />
            <span
              className="toggle-visible-password"
              onClick={togglePassword}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors.newPassword && state.errors.newPassword[0]}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className='mb-3 position-relative' controlId="repeat-password">
            <Form.Label className="small password-icon">
              {translate('cas.change-password.rptPswd')}
            </Form.Label>
            <Form.Control
              type="password"
              name="repeatPassword"
              placeholder={translate('cas.change-password.inputPswd')}
              autoComplete="repeat-password"
              isInvalid={!!state.errors.repeatPassword}
              onChange={() => onChangePassword('repeatPassword')}
              required
            />
            <span
              className="toggle-visible-password"
              onClick={togglePassword}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors.repeatPassword && state.errors.repeatPassword[0]}
            </Form.Control.Feedback>
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            className="w-100 mt-3"
            disabled={
              !state.focus.newPassword ||
              !state.focus.repeatPassword ||
              !state.focus.currentPassword ||
              !!Object.keys(state.errors).length
            }
          >
            {translate('cas.change-password.submit')}
          </Button>
        </Form>
      </div>
    </>
  );
}
